import React, { useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Divider,
  Button,
  Alert,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { useQuery } from "react-query";
import { useIntl } from "react-intl";
import useGlobalContext from "hooks/globalData";
import Loader from "components/Loader";
import translateError from "utils/translateError";
import { FileResponse, SkuPreviewRequest } from "services/KnowfullyAdminClient";

interface SkuPreviewProps {
  skuId: string;
  organizationId: string;
  open: boolean;
  onClose: () => void;
}

const useStyles = () => ({
  feedback: {
    width: "100%",
    margin: "20px 0 20px 0",
  },
});

export function SkuPreview({
  skuId,
  organizationId,
  open,
  onClose,
}: Readonly<SkuPreviewProps>) {
  const { skuService } = useGlobalContext();
  const { formatMessage: t } = useIntl();
  const styles = useStyles();

  const fetchedPreview = useQuery<FileResponse>(
    ["getSkuPreviewPdf", skuId, organizationId],
    () =>
      skuService.getSkuPreviewPdf(
        SkuPreviewRequest.fromJS({
          skuId: skuId,
          organizationId: organizationId,
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        })
      ),
    {
      enabled: open,
    }
  );

  const pdfUrl = useMemo(() => {
    if (fetchedPreview.data?.data) {
      return URL.createObjectURL(fetchedPreview.data.data);
    }
    return null;
  }, [fetchedPreview.data]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        Course Details Preview
        <IconButton
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent style={{ height: "100vh" }} dividers>
        {fetchedPreview.isError && (
          <Alert sx={styles.feedback} severity="error">
            {translateError(fetchedPreview.error, t)}
          </Alert>
        )}
        {fetchedPreview.isFetching ? (
          <Loader />
        ) : (
          pdfUrl && (
            <iframe
              title="skuPreview"
              src={pdfUrl}
              style={{ width: "100%", height: "98%" }}
            />
          )
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
